import { useState, useEffect } from 'react'
import { trialAPI } from '../services/api'
import toast from 'react-hot-toast'
import {
  EnvelopeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

const MILESTONE_LABELS = {
  15: '15 Days',
  10: '10 Days',
  5: '5 Days',
  3: '3 Days',
  2: '2 Days',
  1: '1 Day (Final)',
}

function StatusBadge({ type, children }) {
  const styles = {
    success: 'bg-green-100 text-green-700',
    danger: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    info: 'bg-blue-100 text-blue-700',
    neutral: 'bg-slate-100 text-slate-600',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[type] || styles.neutral}`}>
      {children}
    </span>
  )
}

export default function TrialReminders() {
  const [activeTab, setActiveTab] = useState('validate')
  const [diagnostic, setDiagnostic] = useState(null)
  const [emailLog, setEmailLog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [triggering, setTriggering] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState({})
  const [sendingTo, setSendingTo] = useState({})
  const [selectedMilestone, setSelectedMilestone] = useState({})

  const fetchData = async () => {
    setLoading(true)
    try {
      const [diagRes, logRes] = await Promise.all([
        trialAPI.getDiagnostic(),
        trialAPI.getEmailLog(),
      ])
      setDiagnostic(diagRes.data)
      setEmailLog(logRes.data)
    } catch (err) {
      toast.error('Failed to load trial data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleTriggerCron = async () => {
    setTriggering(true)
    try {
      await trialAPI.triggerCron()
      toast.success('Trial reminder check completed')
      await fetchData()
    } catch (err) {
      toast.error('Failed to trigger trial check')
    } finally {
      setTriggering(false)
    }
  }

  const handleSendManual = async (userId, email, daysRemaining) => {
    setSendingTo(prev => ({ ...prev, [userId]: true }))
    try {
      await trialAPI.sendManual(userId, daysRemaining)
      toast.success(`Reminder sent to ${email}`)
      await fetchData()
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to send to ${email}`)
    } finally {
      setSendingTo(prev => ({ ...prev, [userId]: false }))
    }
  }

  const handleSelectAll = (checked) => {
    if (!diagnostic?.users) return
    const newSelected = {}
    if (checked) {
      diagnostic.users.filter(u => !u.is_expired).forEach(u => {
        newSelected[u.email] = true
      })
    }
    setSelectedUsers(newSelected)
  }

  const handleSendSelected = async () => {
    const usersToSend = diagnostic.users.filter(u =>
      selectedUsers[u.email] && !u.is_expired
    )
    if (usersToSend.length === 0) {
      toast.error('No users selected')
      return
    }

    for (const user of usersToSend) {
      const milestone = selectedMilestone[user.email] || user.next_milestone
      if (!milestone) continue
      await handleSendManual(user.id, user.email, milestone)
    }
    setSelectedUsers({})
  }

  const getDaysColor = (days) => {
    if (days <= 0) return 'text-red-600'
    if (days <= 3) return 'text-red-500'
    if (days <= 5) return 'text-amber-500'
    if (days <= 10) return 'text-blue-500'
    return 'text-slate-600'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Trial Reminders</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor email reminders and manually validate users</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={handleTriggerCron}
            disabled={triggering}
            className="px-4 py-2 text-sm text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
          >
            {triggering ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PaperAirplaneIcon className="h-4 w-4" />}
            {triggering ? 'Running...' : 'Run Cron Now'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {diagnostic?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <UserGroupIcon className="h-4 w-4" />
              Active Starter Users
            </div>
            <div className="text-2xl font-bold text-slate-800">{diagnostic.summary.eligible_users}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <EnvelopeIcon className="h-4 w-4" />
              Emails Sent (Total)
            </div>
            <div className="text-2xl font-bold text-slate-800">{diagnostic.summary.total_emails_sent_ever}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-amber-500 text-sm mb-1">
              <ClockIcon className="h-4 w-4" />
              Due Today
            </div>
            <div className="text-2xl font-bold text-amber-600">{diagnostic.summary.would_send_today}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-red-500 text-sm mb-1">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Expired
            </div>
            <div className="text-2xl font-bold text-red-600">{diagnostic.summary.already_expired}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('validate')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'validate'
                ? 'border-violet-600 text-violet-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <UserGroupIcon className="h-4 w-4" />
              Validate Users
            </span>
          </button>
          <button
            onClick={() => setActiveTab('log')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'log'
                ? 'border-violet-600 text-violet-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <EnvelopeIcon className="h-4 w-4" />
              Email Log
              {emailLog?.total > 0 && (
                <span className="bg-violet-100 text-violet-700 text-xs px-2 py-0.5 rounded-full">{emailLog.total}</span>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Validate Users Tab */}
      {activeTab === 'validate' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Bulk actions */}
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                onChange={(e) => handleSelectAll(e.target.checked)}
                checked={
                  diagnostic?.users?.filter(u => !u.is_expired).length > 0 &&
                  diagnostic?.users?.filter(u => !u.is_expired).every(u => selectedUsers[u.email])
                }
                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-sm text-slate-600">
                {Object.keys(selectedUsers).filter(k => selectedUsers[k]).length} selected
              </span>
            </div>
            {Object.keys(selectedUsers).filter(k => selectedUsers[k]).length > 0 && (
              <button
                onClick={handleSendSelected}
                className="px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 flex items-center gap-1.5"
              >
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                Send to Selected
              </button>
            )}
          </div>

          {/* User table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="px-5 py-3"></th>
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Trial Ends</th>
                  <th className="px-5 py-3">Days Left</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Emails Sent</th>
                  <th className="px-5 py-3">Send Milestone</th>
                  <th className="px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {diagnostic?.users?.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-slate-400">
                      No active starter plan users found
                    </td>
                  </tr>
                )}
                {diagnostic?.users?.map((user) => (
                  <tr key={user.email} className={`hover:bg-slate-50 ${user.is_expired || user.is_deactivated ? 'bg-red-50/50' : ''}`}>
                    <td className="px-5 py-3">
                      <input
                        type="checkbox"
                        disabled={user.is_expired}
                        checked={!!selectedUsers[user.email]}
                        onChange={(e) => setSelectedUsers(prev => ({ ...prev, [user.email]: e.target.checked }))}
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500 disabled:opacity-30"
                      />
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-sm text-slate-800">{user.full_name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600">
                      {user.subscription_end_date
                        ? new Date(user.subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-sm font-semibold ${getDaysColor(user.days_remaining)}`}>
                        {user.days_remaining <= 0 ? 'Expired' : `${user.days_remaining} days`}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {user.is_deactivated ? (
                        <StatusBadge type="danger">Deactivated</StatusBadge>
                      ) : user.is_expired ? (
                        <StatusBadge type="danger">Expired</StatusBadge>
                      ) : user.is_at_milestone ? (
                        <StatusBadge type="warning">At Milestone</StatusBadge>
                      ) : (
                        <StatusBadge type="success">Active</StatusBadge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.already_sent_milestones.length === 0 ? (
                          <span className="text-xs text-slate-400">None</span>
                        ) : (
                          user.already_sent_milestones.map(m => (
                            <span key={m} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                              <CheckCircleIcon className="h-3 w-3" />
                              {m}d
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <select
                        disabled={user.is_expired}
                        value={selectedMilestone[user.email] || user.next_milestone || ''}
                        onChange={(e) => setSelectedMilestone(prev => ({ ...prev, [user.email]: parseInt(e.target.value) }))}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white disabled:opacity-40 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="" disabled>Select</option>
                        {[15, 10, 5, 3, 2, 1].map(m => (
                          <option key={m} value={m} disabled={user.already_sent_milestones.includes(m)}>
                            {MILESTONE_LABELS[m]} {user.already_sent_milestones.includes(m) ? '(sent)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        disabled={user.is_expired || sendingTo[user.id] || !(selectedMilestone[user.email] || user.next_milestone)}
                        onClick={() => {
                          const milestone = selectedMilestone[user.email] || user.next_milestone
                          handleSendManual(user.id, user.email, milestone)
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 flex items-center gap-1.5"
                      >
                        {sendingTo[user.id] ? (
                          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <PaperAirplaneIcon className="h-3.5 w-3.5" />
                        )}
                        Send
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Log Tab */}
      {activeTab === 'log' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50">
                  <th className="px-5 py-3">Recipient</th>
                  <th className="px-5 py-3">Milestone</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Sent At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {emailLog?.logs?.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-slate-400">
                      No emails have been sent yet
                    </td>
                  </tr>
                )}
                {emailLog?.logs?.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 text-sm text-slate-800">{log.email}</td>
                    <td className="px-5 py-3">
                      <StatusBadge type={log.days_remaining <= 3 ? 'danger' : log.days_remaining <= 5 ? 'warning' : 'info'}>
                        {MILESTONE_LABELS[log.days_remaining] || `${log.days_remaining} Days`}
                      </StatusBadge>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        Sent
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-500">
                      {log.sent_at
                        ? new Date(log.sent_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errors */}
      {diagnostic?.errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
            <XCircleIcon className="h-4 w-4" />
            Diagnostic Errors
          </h3>
          {diagnostic.errors.map((err, i) => (
            <pre key={i} className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{err}</pre>
          ))}
        </div>
      )}
    </div>
  )
}
